import {useHistoryOperationModesByIdQuery} from '@domain/assets'
import {getPeakLoadWindows, usePeakLoadWindowsQuery} from '@domain/electricity'
import {useHistoryStoragesWithMaterialQuery} from '@domain/material-storage'
import {useMaterialsRecipesById} from '@domain/materials'
import {
  COST_AVOIDANCE_FOR_RANGE_QUERY_KEY,
  ELECTRICITY_QUERY_KEY,
  SCHEDULE_QUERY_KEY,
  StockDevelopmentQueryKey,
  useElectricityQuery,
  useUpdateScheduleItemMutation
} from '@domain/schedule'
import type {Schedule, ScheduleItem} from '@domain/schedule'
import {useUpmPlantContext, useUpmPlantTimezone} from '@hconnect/upm'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useQueryClient} from '@tanstack/react-query'
import {useSelectedScheduleItemId} from '@widgets/planning-schedule-chart'
import type {AssetWithOperationMode} from '@widgets/planning-schedule-chart'
import {Moment} from 'moment-timezone'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../../consts'
import {useTrackPlannerEvent} from '../../../helpers'
import {
  doesOverlappingItemExist,
  getDefaultSelectedSilos,
  getScheduleItemPowerConsumptionQuaterlyDict
} from '../../../selectors'

type ScheduleContext = {previousState: Schedule}

interface UpdateScheduleItemParams {
  startOfPlan: Moment
  endOfPlan: Moment
  assetDataByScheduleItemId: Record<string, AssetWithOperationMode> | undefined
}

export const useUpdateScheduleItem = ({
  startOfPlan,
  endOfPlan,
  assetDataByScheduleItemId
}: UpdateScheduleItemParams) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const queryClient = useQueryClient()
  const {plantId: plantCode} = useUpmPlantContext()
  const timezoneId = useUpmPlantTimezone()
  const trackPlannerEvent = useTrackPlannerEvent()

  const {data: peakLoadWindows} = usePeakLoadWindowsQuery({
    plantId: plantCode,
    timeFrame: [startOfPlan, endOfPlan],
    throwOnError: false
  })

  const {data: electricity} = useElectricityQuery({
    plantId: plantCode,
    timezoneId,
    timeFrame: [startOfPlan, endOfPlan],
    throwOnError: false
  })

  const {data: operationModesById} = useHistoryOperationModesByIdQuery({
    plantId: plantCode,
    timeFrame: [startOfPlan, endOfPlan]
  })
  const {data: recipesById} = useMaterialsRecipesById({plantId: plantCode, throwOnError: false})
  const {data: storagesWithMaterial} = useHistoryStoragesWithMaterialQuery({
    plantId: plantCode,
    timeFrame: [startOfPlan, endOfPlan],
    throwOnError: false
  })

  const queryVariables = useMemo(
    () => ({plantCode, start: startOfPlan.toISOString(), end: endOfPlan.toISOString()}),
    [plantCode, startOfPlan, endOfPlan]
  )

  const {setScheduleItemId} = useSelectedScheduleItemId()

  const {mutate, isPending: isUpdateScheduleItemLoading} = useUpdateScheduleItemMutation({
    onMutate: async (variables) => {
      await queryClient.cancelQueries({queryKey: [SCHEDULE_QUERY_KEY]})
      const previousState = queryClient.getQueryData<Schedule>([SCHEDULE_QUERY_KEY, queryVariables])
      const id = variables.scheduleItemId
      if (previousState) {
        const updatedSchedule: Schedule = {
          ...previousState,
          schedules: {
            ...previousState.schedules,
            [id]: {
              ...previousState.schedules[id],
              ...variables.scheduleItemDTO
            }
          }
        }
        queryClient.setQueryData([SCHEDULE_QUERY_KEY, queryVariables], updatedSchedule)
      }
      return {previousState} as ScheduleContext
    },
    onError: (error, _, context) => {
      spawnErrorToast((error.response?.data as {detail?: string} | undefined)?.detail)
      const ctx = context as ScheduleContext | undefined
      if (ctx) {
        queryClient.setQueryData([SCHEDULE_QUERY_KEY, queryVariables], ctx.previousState)
      }
    },
    onSuccess: ({splitItemId}) => {
      void queryClient.invalidateQueries({
        queryKey: [COST_AVOIDANCE_FOR_RANGE_QUERY_KEY],
        refetchType: 'all'
      })
      if (splitItemId) {
        spawnSuccessToast(t('success.scheduleItemSplit'))
        // when splitting we need to update selected schedule item id to replace active schedule item in edit dialog
        setScheduleItemId(splitItemId)
      }
    },
    onSettled: (data, _, variables) => {
      trackPlannerEvent({name: 'onEditScheduleItem'})
      void queryClient.invalidateQueries({queryKey: [SCHEDULE_QUERY_KEY], refetchType: 'all'})
      void queryClient.invalidateQueries({
        queryKey: [ELECTRICITY_QUERY_KEY, {plantCode: variables.plantCode}],
        refetchType: 'all'
      })
      void queryClient.invalidateQueries({
        queryKey: [StockDevelopmentQueryKey],
        refetchType: 'all'
      })
    }
  })

  const updateScheduleItem = useMemo(
    () => (scheduleItem: ScheduleItem) => {
      const schedule = queryClient.getQueryData<Schedule>([SCHEDULE_QUERY_KEY, queryVariables])
      if (
        !electricity ||
        !schedule ||
        !recipesById ||
        !storagesWithMaterial ||
        !assetDataByScheduleItemId ||
        !operationModesById ||
        !peakLoadWindows
      )
        return
      const prevScheduleItem = schedule.schedules[scheduleItem.id]

      // Since BE doesn't track operation mode change to selected silo relation we need to check it ourselves and reset selected silos on each operation mode change
      const activeOperationMode = operationModesById[scheduleItem.assetOperationModeId]
      if (prevScheduleItem.assetOperationModeId !== scheduleItem.assetOperationModeId) {
        scheduleItem.selectedSilos = getDefaultSelectedSilos(
          activeOperationMode,
          recipesById,
          storagesWithMaterial
        )
      }
      const prevTotalPowerConsumption = electricity.planned

      const powerConsumptionForPrevItemDict = getScheduleItemPowerConsumptionQuaterlyDict(
        prevScheduleItem,
        assetDataByScheduleItemId[prevScheduleItem.id].operationMode
      )

      const powerConsumptionForUpdatedItemDict = getScheduleItemPowerConsumptionQuaterlyDict(
        scheduleItem,
        activeOperationMode
      )
      const adjustedPowerConsumption = prevTotalPowerConsumption.map((energyConsumption) => {
        const prevItemPower = powerConsumptionForPrevItemDict[energyConsumption.dateTimeIso] || 0
        const updatedItemPower =
          powerConsumptionForUpdatedItemDict[energyConsumption.dateTimeIso] || 0
        const diff = updatedItemPower - prevItemPower
        return {...energyConsumption, power: energyConsumption.power + diff}
      })
      const adjustedPeakLoadWindows = getPeakLoadWindows({
        peakLoadWindows,
        powerConsumption: adjustedPowerConsumption
      })
      const hasPeakLoadWindowsViolated = adjustedPeakLoadWindows.some((plw) => plw.violated)

      const hasOverlap = doesOverlappingItemExist(schedule.schedules, scheduleItem, scheduleItem.id)
      if (hasOverlap) {
        spawnErrorToast(t('error.overlappingItemExists'))
        void queryClient.invalidateQueries({
          queryKey: [SCHEDULE_QUERY_KEY, queryVariables],
          refetchType: 'all'
        })
        return
      }
      if (hasPeakLoadWindowsViolated) {
        spawnErrorToast(t('error.planViolatesPeakLoadWindow'))
        void queryClient.invalidateQueries({
          queryKey: [SCHEDULE_QUERY_KEY, queryVariables],
          refetchType: 'all'
        })
        return
      }

      mutate({
        plantCode,
        scheduleId: schedule.scheduleId,
        scheduleItemDTO: scheduleItem,
        scheduleItemId: scheduleItem.id
      })
    },
    [
      queryClient,
      electricity,
      plantCode,
      queryVariables,
      assetDataByScheduleItemId,
      operationModesById,
      recipesById,
      storagesWithMaterial,
      peakLoadWindows,
      mutate,
      t
    ]
  )

  return {updateScheduleItem, isUpdateScheduleItemLoading}
}
